import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api/client';
import { Button, Card, Loading, confirmAction, toast } from '../components/ui';
import { DataTable } from '../components/DataTable';

interface Profile {
    id: number;
    name: string;
    description: string | null;
    isDefault: boolean;
    recordVideo: boolean;
    blockKeyboardInput: boolean;
    requireDeviceRehearsal: boolean;
    _count?: { rules: number };
}

export function Proctoring() {
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['proctoring'],
        queryFn: () => api.get<Profile[]>('/api/admin/proctoring')
    });

    const remove = useMutation({
        mutationFn: (id: number) => api.post(`/api/admin/proctoring/${id}/delete`),
        onSuccess: async () => {
            toast('Camera rules retired.');
            await queryClient.invalidateQueries({ queryKey: ['proctoring'] });
        },
        // The server refuses when templates still use it, or when it is the default. Its message
        // explains which, so it is shown as-is.
        onError: (caught: Error) => toast(caught.message, 'error')
    });

    return (
        <Card
            heading
            title="Camera rules"
            lede="What is captured during an interview, which pre-flight checks are enforced, and how each signal affects the integrity score. Interview formats pick a set of rules."
            action={<Link to="/proctoring/new"><Button>Add profile</Button></Link>}
        >
            <p className="mb-4 text-sm text-slate-600">
                A profile is frozen onto each interview when it is created, so retuning one here never
                changes what an existing report means.
            </p>

            {isLoading ? <Loading /> : (
                <DataTable
                    rows={data ?? []}
                    rowKey={(row) => row.id}
                    empty="No profiles yet."
                    columns={[
                        {
                            header: 'Name', sortBy: (row) => row.name,
                            cell: (row) => (
                                <Link
                                    to={`/proctoring/${row.id}`}
                                    className="font-medium text-[var(--brand-primary)] hover:underline"
                                >
                                    {row.name}
                                    {row.isDefault && (
                                        <span className="ml-2 text-xs font-normal text-slate-500">default</span>
                                    )}
                                </Link>
                            )
                        },
                        { header: 'Description', sortBy: (row) => row.description, cell: (row) => row.description ?? '—' },
                        { header: 'Signals on', cell: (row) => row._count?.rules ?? 0 },
                        { header: 'Records video', cell: (row) => row.recordVideo ? 'Yes' : 'No' },
                        {
                            header: 'Keyboard locked',
                            cell: (row) => row.blockKeyboardInput ? 'Yes' : 'No'
                        },
                        {
                            header: 'Actions',
                            className: 'text-right',
                            cell: (row) => (
                                // The name is a link too, but every other list screen offers an
                                // explicit Edit next to Retire. A row whose only button is the
                                // destructive one reads as having no editor at all.
                                <div className="flex justify-end gap-2">
                                    <Link to={`/proctoring/${row.id}`}>
                                        <Button variant="secondary">
                                            <i className="bi bi-pencil" aria-hidden="true" />
                                            Edit
                                        </Button>
                                    </Link>

                                    <Button
                                        variant="danger-quiet"
                                        onClick={async () => {
                                            const confirmed = await confirmAction({
                                                title: `Remove ${row.name}?`,
                                                confirmText: 'Retire these rules',
                                                danger: true
                                            });
                                            if (confirmed) remove.mutate(row.id);
                                        }}
                                    >
                                        Retire
                                    </Button>
                                </div>
                            )
                        }
                    ]}
                />
            )}
        </Card>
    );
}
